/**
 * Copyright Aquera Inc 2023
 *
 * This source code is licensed under the BSD-3-Clause license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { LitElement, html, css, CSSResultGroup } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { NileOption } from '../nile-option';
import styles from './nile-select.css';
import { HasSlotController } from '../slot';
import { watch } from '../watch';

/**
 * @summary Selects allow you to choose items from a menu of predefined options.
 *
 * @event nile-change - Emitted when the control's value changes.
 */

type selectOption = {
  name: string;
  value: string | number;
};

@customElement('nile-select')
export class NileSelect extends LitElement {
  static override styles: CSSResultGroup = styles;

  @property() dropDownOptionsArray: selectOption[] = [];

  // @property() value: string | number = '';

  /**
   * The current value of the select, submitted as a name/value pair with form data. When `multiple` is enabled, the
   * value will be a space-delimited list of values based on the options selected.
   */
  @property({
    converter: {
      fromAttribute: (value: string) => value.split(' '),
      toAttribute: (value: string[]) => value.join(' '),
    },
  })
  value: string | string[] = '';

  /** Allows more than one option to be selected. */
  @property({ type: Boolean, reflect: true }) multiple = false;

  /** Allows more than one option to be selected. */
  @property({ type: Boolean, reflect: true }) placeholder =
    'Select your option';

  /** Allows to search on option */
  @property({ type: Boolean, reflect: true }) searchEnabled = false;

  /** Miximum tags to show*/
  @property({ type: Number, reflect: true }) maxTags = 3;

  @state() isOpen: boolean = false;
  @state() private hasFocus = false;
  @state() displayLabel = '';
  @state() currentOption: NileOption | undefined;
  @state() selectedOptions: NileOption[] = [];
  @state() searchString: string = '';
  @state() showSelected: boolean = false;

  private readonly hasSlotController = new HasSlotController(
    this,
    'help-text',
    'label'
  );

  constructor() {
    super();
  }

  render() {
    const hasIconSlot = this.hasSlotController.test('parent-icon');
    const labelClass = `label ${hasIconSlot ? 'with-icon' : ''} ${this.multiple && this.value.length !== 0 ? 'hidden' : ''}`;

    return html`
      <div class="select-container" @click="${this.toggleOptions}">
        <span class="parent-icon">
          <slot name="parent-icon"></slot>
        </span>
        ${true ? html`
          <span class="${labelClass}">
            ${this.displayLabel.toString().length > 0 ? html`
              <span class="selected">${this.displayLabel}</span>
            ` : this.placeholder}
          </span>
        ` : ''}
        ${this.multiple ? html`
          <span class="label multi ${hasIconSlot ? 'with-icon' : ''}">
            ${this.selectedOptions.slice(0, this.maxTags).map((option) => html`
              <nile-tag removable="true" @nile-remove="${(event: CustomEvent<any>) => this.handleTagRemove(event, option)}">
                <span name="content">${option.getTextLabel()}</span>
              </nile-tag>
            `)}
            ${this.selectedOptions.length > this.maxTags ? html`
              <nile-tag>
                <span name="content">+${this.selectedOptions.length - this.maxTags}</span>
              </nile-tag>
            ` : ''}
          </span>
        ` : ''}
        <span class="icon ${this.isOpen ? 'rotate-down' : ''}">
          <nile-icon name="arrowdown" size="16"></nile-icon>
        </span>
        ${this.isOpen ? html`
          <div class="options">
            ${this.searchEnabled ? html`
              <input class="select-search" type="text" autofocus @input="${this.onSearch}" value="${this.searchString}" @click="${this.handleSearchInputClick}" />
            ` : ''}
            <slot tabindex="-1" @click="${this.handleOptionClick}"></slot>
            ${this.multiple ? html`
              <div class="footer" @click="${this.handleFooterClick}">
                <span @click="${this.toggleShowSelected}" style="cursor: pointer;">
                  <input type="checkbox" style="pointer-events: none;" ?disabled="${this.selectedOptions.length === 0}" ?checked="${this.showSelected}" />
                  Show Selected
                </span>
                <span class="clear" @click="${this.unSlectAll}">clear all</span>
              </div>
            ` : ''}
          </div>
        ` : ''}
      </div>
    `;
  }


  /**
   * Connect the component and bind handleOutsideClick to the document click event.
   */
  connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('click', this.handleOutsideClick.bind(this));
  }

  /**
   * Disconnect the component and unbind handleOutsideClick from the document click event.
   */
  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('click', this.handleOutsideClick.bind(this));
  }

  /**
   * Handles the click event outside the component, and closes the component if necessary.
   * @param event - The click event.
   */
  handleOutsideClick(event: MouseEvent): void {
    if (!this.contains(event.target as Node)) {
      this.isOpen = false;
    }
  }

  /**
   * Handles the click event on the footer.
   * @param event - The click event.
   */
  handleFooterClick(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
  }

  // onSearch(event: any) {
  //   const allOptions = this.getAllOptions();
  //   // const newSelectedOptions = Array.isArray(option) ? option : [option];
  //   const newSelectedOptions = allOptions.filter(
  //     el => !el.childNodes[1]?.textContent?.includes(event.target?.value)
  //   );

  //   // Clear existing selection
  //   allOptions.forEach(el => (el.hidden = false));

  //   // Set the new selection
  //   if (newSelectedOptions.length) {
  //     newSelectedOptions.forEach(el => (el.hidden = true));
  //   }
  // }

  onSearch(event: Event): void {
    const allOptions = this.getAllOptions();
    const inputValue = (event.target as HTMLInputElement).value
      .trim()
      .toLowerCase();
    const newSelectedOptions = allOptions.filter((option: HTMLElement) => {
      const textContent = option.childNodes[1]?.textContent
        ?.trim()
        .toLowerCase();
      return textContent && !textContent.includes(inputValue);
    });

    // Reset all options to be visible
    allOptions.forEach(option => {
      option.hidden = false;
    });

    // Hide options that don't match the search query
    newSelectedOptions.forEach(option => {
      option.hidden = true;
    });
  }

  toggleOptions(): void {
    this.isOpen = !this.isOpen;
  }

  handleSearchInputClick(event: Event) {
    event.stopPropagation();
    event.preventDefault();
  }

  private handleTagRemove(event: CustomEvent, option: NileOption) {
    event.stopPropagation();
    this.toggleOptionSelection(option, false);
    // this.emit('nile-change');
  }

  // selectOption(option: selectOption, event: Event) {
  handleOptionClick(event: MouseEvent): void {
    event.stopPropagation();

    const target = event.target as HTMLElement;
    const option = target.closest<NileOption>('nile-option');

    if (!option) {
      return;
    }

    if (this.multiple) {
      this.toggleOptionSelection(option);
    } else {
      this.setSelectedOptions(option);
      this.isOpen = false;
    }
  }

  // Toggles an option's selected state
  private toggleOptionSelection(option: NileOption, force?: boolean): void {
    if (force === true || force === false) {
      option.selected = force;
    } else {
      option.selected = !option.selected;
    }

    this.selectionChanged();
  }

  /**
   * Sets the selected option(s)
   * @param option - the selected option(s)
   */
  private setSelectedOptions(option: NileOption | NileOption[]): void {
    // get all <nile-option> elements
    const allOptions = this.getAllOptions();
    // convert option to array if not already an array
    const newSelectedOptions = Array.isArray(option) ? option : [option];

    // clear existing selection
    allOptions.forEach(el => (el.selected = false));

    // set the new selection
    if (newSelectedOptions.length) {
      newSelectedOptions.forEach(el => (el.selected = true));
    }

    // update selection, value, and display label
    this.selectionChanged();
  }

  /**
   * Gets an array of all <nile-option> elements
   * @returns an array of <nile-option> elements
   */
  private getAllOptions(): NileOption[] {
    return [...this.querySelectorAll<NileOption>('nile-option')];
  }

  /**
   * Gets the text content of the element and trims it
   * @returns the trimmed text content
   */
  getTextLabel(): string {
    return (this.textContent ?? '').trim();
  }

  /**
   * This method must be called whenever the selection changes. It will update the selected options cache, the current
   * value, and the display value
   */
  private selectionChanged(event?: Event): void {
    // Update selected options cache
    this.selectedOptions = this.getAllOptions().filter(el => el.selected);

    // Update the value and display label
    if (this.multiple) {
      // For multi-select mode, update value as an array of selected options' values
      this.value = this.selectedOptions.map(el => el.value);
      // Update display label as number of options selected
      // if (this.placeholder && this.value.length === 0) {
      //   // When no items are selected, keep the value empty so the placeholder shows
      //   this.displayLabel = '';
      // } else {
      // this.displayLabel = this.localize.term('numOptionsSelected', this.selectedOptions.length);
      // }
    } else {
      // For single-select mode, update value and display label with the first selected option's value and text content
      this.value = this.selectedOptions[0]?.value ?? '';
      this.displayLabel = this.selectedOptions[0]?.getTextLabel() ?? '';
    }

    // Dispatch a custom event to notify changes
    this.dispatchEvent(
      new CustomEvent('nile-change', {
        bubbles: true,
        composed: true,
        detail: {
          srcEvent: event,
          value: this.value,
        },
      })
    );

    // Update validity
    // this.updateComplete.then(() => {
    //   this.formControlController.updateValidity();
    // });
  }

  /**
   * Sets the current option, which is the option the user is currently interacting with (e.g. via keyboard). Only one
   * option may be "current" at a time.
   * @param option - the target option to select
   */
  private setCurrentOption(option: NileOption | null): void {
    const allOptions = this.getAllOptions();

    // Clear selection
    allOptions.forEach(el => {
      el.current = false;
      el.tabIndex = -1;
    });

    // Select the target option
    if (option) {
      this.currentOption = option;
      option.current = true;
      option.tabIndex = 0;
      option.focus();
    }
  }

  /**
   * Toggles the display of selected options in the dropdown
   * @param event - the event object
   */
  private toggleShowSelected(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    // Only toggle if there are selected options
    if (this.selectedOptions?.length === 0) {
      return;
    }
    // Toggle the showSelected property
    this.showSelected = !this.showSelected;

    // Hide unselected options if showSelected is true
    const allOptions = this.getAllOptions();
    allOptions.forEach(el => {
      if (!el.selected) {
        el.hidden = this.showSelected;
      }
    });
  }

  /**
   * Handles the checkbox click event and toggles the display of selected options in the dropdown
   * @param event - the event object
   */
  private handleCheckBox(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.toggleShowSelected(event);
  }

  /**
   * Deselects all options and updates the value, selectedOptions, and displayLabel properties accordingly
   */
  private unSlectAll(): void {
    this.showSelected = false;
    const allOptions = this.getAllOptions();
    allOptions.forEach(el => {
      el.selected = false;
      el.hidden = false;
    });
    this.value = '';
    this.selectionChanged();
  }

  // get selectedOption(): selectOption {
  //   const selectedOption = this.dropDownOptionsArray.filter(
  //     option => option.value == this.value
  //   );
  //   return selectedOption[0];
  // }

  @watch('value', { waitUntilFirstUpdate: true })
  handleValueChange() {
    const allOptions = this.getAllOptions();
    const value = Array.isArray(this.value) ? this.value : [this.value];

    // Select only the options that match the new value
    this.setSelectedOptions(allOptions.filter(el => value.includes(el.value)));
  }

  @watch('isOpen', { waitUntilFirstUpdate: true })
  handleOpenChange() {
    const allOptions = this.getAllOptions();
    allOptions.forEach(el => (el.hidden = false));
    this.showSelected = false;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nile-select': NileSelect;
  }
}
